import React, { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from './input.jsx';
import { Button } from './button.jsx';
import { Label } from './label.jsx';
import { cn } from '../../lib/utils';
import { dataApi } from '../../services/api';
import { PresenceBadge, getPresenceFieldStyle } from './presence-badge.jsx';

/**
 * PartLookupInput - A searchable dropdown for selecting vehicle parts from insurer line allowances
 * 
 * @param {Object} props
 * @param {string} props.label - Label for the input
 * @param {string} props.value - Current part name value
 * @param {Function} props.onChange - Callback when part is selected (receives part name string)
 * @param {Function} [props.onPartSelected] - Callback when part is selected from dropdown (receives full part object with $id, removeRefitAccessParts, etc.)
 * @param {string} props.insurerConfigId - The insurer config ID to fetch parts for
 * @param {string} [props.zone] - Optional vehicle zone to filter parts by (e.g., 'front', 'rear')
 * @param {string} [props.placeholder] - Placeholder text
 * @param {boolean} [props.required] - Whether the field is required
 * @param {boolean} [props.disabled] - Whether the input is disabled
 * @param {boolean} [props.commitOnBlur] - Whether to commit the value on blur (default: true)
 * @param {boolean} [props.clearOnChange] - Whether to clear the input after onChange (default: false)
 * @param {string} [props.error] - Error message to display
 * @param {string} [props.className] - Additional CSS classes
 * @param {object} [props.editor] - Another user currently editing this field ({ user_id, user_name, user_initials })
 * @param {boolean} [props.isCurrentUser] - Whether `editor` is the current user (suppresses the badge)
 * @param {Function} [props.onFocus] - Called in addition to the internal focus handling
 * @param {Function} [props.onBlur] - Called in addition to the internal blur handling
 */
const PartLookupInput = ({
  label,
  value,
  onChange,
  onPartSelected,
  insurerConfigId,
  zone = null,
  placeholder = 'Search for a part...',
  required = false,
  disabled = false,
  commitOnBlur = true,
  clearOnChange = false,
  error,
  className = '',
  editor = null,
  isCurrentUser = false,
  onFocus,
  onBlur
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [searchResults, setSearchResults] = useState([]);
  const [allParts, setAllParts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingParts, setLoadingParts] = useState(false);
  const [isHoveredOrFocused, setIsHoveredOrFocused] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  // Pending blur-commit timer. Selecting a dropdown item must cancel it, otherwise
  // the stale timer (scheduled by the blur that fires as the item is clicked) commits
  // the partial search term ~150ms later and reverts the freshly selected part.
  const blurCommitTimer = useRef(null);

  const showPresenceBadge = !!editor && !isCurrentUser;

  // Update search term when value changes from parent
  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  // Cancel any pending blur commit on unmount so it can't fire after teardown.
  useEffect(() => () => {
    if (blurCommitTimer.current) clearTimeout(blurCommitTimer.current);
  }, []);

  // Load parts when insurerConfigId or zone changes.
  //
  // No insurerConfigId is a private job rather than a missing argument: there is
  // no guide to read, but the shop's own line sheet still applies and is the
  // only source of lines there is. Bailing out here is what left the lookup
  // empty on every private job.
  useEffect(() => {
    const loadParts = async () => {
      setLoadingParts(true);
      try {
        const parts = await dataApi.getInsurerParts(insurerConfigId, zone, null);
        setAllParts(parts);
        
        // If dropdown is open and no search term, show all parts
        if (isOpen && !searchTerm.trim()) {
          setSearchResults(parts);
        }
      } catch (error) {
        console.error('Error loading parts:', error);
        setAllParts([]);
        setSearchResults([]);
      } finally {
        setLoadingParts(false);
      }
    };

    loadParts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insurerConfigId, zone]);

  // Filter parts based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults(allParts);
      setLoading(false);
      return;
    }

    setLoading(true);
    const term = searchTerm.toLowerCase();
    
    const filtered = allParts.filter(part => {
      const lineName = (part.lineName || '').toLowerCase();
      const searchTerms = (part.searchTerms || '').toLowerCase();
      
      return lineName.includes(term) || 
             searchTerms.includes(term);
    });

    setSearchResults(filtered);
    setLoading(false);
  }, [searchTerm, allParts]);

  // Handle clicks outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    // Always allow free text, but show suggestions
    if (term.trim() || allParts.length > 0) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleItemSelect = (part) => {
    const partName = part.lineName || '';

    // A selection supersedes the blur-commit that was scheduled when this click
    // stole focus from the input; without this the stale timer reverts the value
    // to the partial search term the user typed to find this item.
    if (blurCommitTimer.current) {
      clearTimeout(blurCommitTimer.current);
      blurCommitTimer.current = null;
    }

    // Notify parent with full part object BEFORE onChange so parent can capture the ID
    if (onPartSelected) {
      onPartSelected(part);
    }
    
    if (clearOnChange) {
      setSearchTerm('');
      setIsOpen(false);
      onChange(partName);
    } else {
      setSearchTerm(partName);
      setIsOpen(false);
      onChange(partName);
    }
  };

  const handleClear = () => {
    if (blurCommitTimer.current) {
      clearTimeout(blurCommitTimer.current);
      blurCommitTimer.current = null;
    }
    setSearchTerm('');
    setIsOpen(false);
    onChange('');
    // Keep focus on input after clearing
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeyDown = (e) => {
    // Allow Enter to confirm current text (even if not from list)
    if (e.key === 'Enter' && searchTerm.trim()) {
      setIsOpen(false);
      onChange(searchTerm);
      
      if (clearOnChange) {
        setSearchTerm('');
      }
    }
  };

  const handleBlur = () => {
    setIsHoveredOrFocused(false);
    onBlur?.();

    if (!commitOnBlur) {
      return;
    }
    
    // Small delay to allow click on dropdown item. A dropdown selection cancels
    // this timer (see handleItemSelect), so it only fires for free-typed text.
    blurCommitTimer.current = setTimeout(() => {
      blurCommitTimer.current = null;
      // Commit whatever is typed as the value
      if (searchTerm !== value && searchTerm.trim()) {
        onChange(searchTerm);

        if (clearOnChange) {
          setSearchTerm('');
        }
      }
    }, 150);
  };

  return (
    <div className={cn("relative", className)}>
      {label && (
        <Label className="block font-medium text-xs mb-1">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      <div className="relative" ref={dropdownRef}>
        <div className="relative flex items-center">
          <Input
            ref={inputRef}
            type="text"
            className={cn(
              "pr-20 h-8 text-sm text-ellipsis transition-shadow duration-200",
              error && "border-destructive focus-visible:ring-destructive",
              disabled && "cursor-not-allowed opacity-50"
            )}
            // Only styled while someone else holds the field, so the input's
            // own focus-visible ring is left intact the rest of the time.
            style={showPresenceBadge ? getPresenceFieldStyle(editor.user_id) : undefined}
            value={searchTerm}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onFocus={() => {
              setIsHoveredOrFocused(true);
              if (allParts.length > 0) {
                setIsOpen(true);
              }
              onFocus?.();
            }}
            onMouseEnter={() => setIsHoveredOrFocused(true)}
            onMouseLeave={() => {
              if (document.activeElement !== inputRef.current) {
                setIsHoveredOrFocused(false);
              }
            }}
            placeholder={disabled ? 'Select an insurer first' : placeholder}
            disabled={disabled}
            autoComplete="off"
          />

          {searchTerm && !disabled && isHoveredOrFocused && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-8 h-6 w-6 hover:bg-accent text-muted-foreground hover:text-foreground"
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent blur from firing
                handleClear();
              }}
              onMouseEnter={() => setIsHoveredOrFocused(true)}
              title="Clear selection"
              tabIndex={-1}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}

          {!disabled && isHoveredOrFocused && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 h-6 w-6 hover:bg-accent text-muted-foreground hover:text-foreground"
              onClick={() => setIsOpen(!isOpen)}
              onMouseEnter={() => setIsHoveredOrFocused(true)}
              title="Search parts"
              tabIndex={-1}
            >
              <Search className="h-3.5 w-3.5" />
            </Button>
          )}

          {showPresenceBadge && <PresenceBadge editor={editor} />}
        </div>

        {isOpen && (
          <div className="absolute top-full left-0 min-w-80 bg-popover border border-border rounded-md shadow-md z-50 max-h-80 overflow-y-auto mt-1">
            {loadingParts && (
              <div className="flex items-center gap-2 p-3 text-muted-foreground text-xs">
                <div className="w-3 h-3 border-2 border-muted border-t-primary rounded-full animate-spin"></div>
                Loading parts...
              </div>
            )}

            {!loadingParts && loading && (
              <div className="flex items-center gap-2 p-3 text-muted-foreground text-xs">
                <div className="w-3 h-3 border-2 border-muted border-t-primary rounded-full animate-spin"></div>
                Searching...
              </div>
            )}

            {!loadingParts && !loading && searchResults.length > 0 && (
              <div className="max-h-72 overflow-y-auto">
                {searchResults.map((part, index) => (
                  <div
                    key={part.$id || part.id || index}
                    className="flex flex-col p-2 cursor-pointer border-b border-border last:border-b-0 hover:bg-accent transition-colors"
                    onClick={() => handleItemSelect(part)}
                  >
                    <div className="flex items-center gap-1.5">
                      <div className="font-medium text-xs text-foreground">
                        {part.lineName || 'Unnamed Part'}
                      </div>
                      {/* Which sheet this came from. An assessor choosing
                          between a guide line and one of the shop's own should
                          be able to see which is which — the insurer pays for
                          one of them and argues about the other. */}
                      {part.isOrgLine && (
                        <span
                          className="text-[9px] uppercase tracking-wide text-muted-foreground border rounded px-1 leading-4 whitespace-nowrap"
                          title="One of your own lines, not from the insurer's guide"
                        >
                          Ours
                        </span>
                      )}
                    </div>
                    {part.searchTerms && (
                      <div className="text-xs text-muted-foreground truncate">
                        {part.searchTerms}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {!loadingParts && !loading && searchTerm.trim() && searchResults.length === 0 && (
              <div className="p-3 text-xs text-muted-foreground text-center">
                No matching parts found for "{searchTerm}"
                <div className="mt-1 text-xs">
                  You can still use this text as the part name
                </div>
              </div>
            )}

            {!loadingParts && !loading && !searchTerm.trim() && searchResults.length === 0 && (
              <div className="p-3 text-xs text-muted-foreground text-center">
                {zone
                  ? `No parts available in the ${zone} section`
                  : 'No parts available'
                }
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="text-xs text-destructive mt-1">
          {error}
        </div>
      )}
    </div>
  );
};

export default PartLookupInput;
